import React, { useEffect, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { X, BarChart3, Ruler, ShoppingCart, Clock, Eye } from "lucide-react";

const StatBox = ({ icon: Icon, label, value, accent = "text-[#09637E]", testid }) => (
  <div className="bg-[#EBF4F6] rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#666666] text-sm font-bold mb-1">
      <Icon size={16} /> {label}
    </div>
    <div className={`text-2xl font-extrabold ${accent}`}>{value}</div>
  </div>
);

const PhotoDashboardModal = ({ photoId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/stats/photo/${photoId}`);
        if (alive) setData(r.data);
      } catch (e) {
        toast.error(arabicError(e));
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [photoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = data?.photo;
  const metersSold = data?.meters_sold ?? 0;
  const completed = data?.completed_sales ?? 0;
  const pending = data?.pending_reservations ?? 0;
  const recent = data?.recent_sales ?? [];
  const searchCount = p?.search_count ?? 0;
  const revenue = (parseFloat(p?.price_usd ?? 0) * parseFloat(metersSold)).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} dir="rtl">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="photo-dashboard-modal">
        <div className="sticky top-0 bg-white border-b border-[#7AB2B2] p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-[#09637E]" size={22} />
            <h3 className="text-xl font-extrabold text-[#333333]">لوحة المنتج</h3>
          </div>
          <button onClick={onClose} data-testid="close-photo-dashboard"
            className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
        </div>

        {loading || !p ? (
          <div className="p-12 text-center text-[#666666]">جارٍ التحميل...</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Product summary */}
            <div className="flex gap-4 bg-[#EBF4F6] rounded-xl p-4">
              <img src={p.image_url} alt="" className="w-28 h-28 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-[#333333] truncate" title={p.display_name}>{p.display_name}</div>
                <div className="text-sm text-[#666666]">{p.category} · {p.type} · {p.country}</div>
                <div className="text-sm text-[#666666]">{p.size} · {p.color || "—"}</div>
                <div className="text-lg font-extrabold text-[#09637E] mt-1" dir="ltr">${parseFloat(p.price_usd ?? 0).toFixed(2)} / م</div>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatBox icon={Ruler} label="الكمية المباعة" value={`${metersSold} م`} accent="text-green-700" testid="stat-meters-sold" />
              <StatBox icon={ShoppingCart} label="طلبات تم بيعها" value={completed} testid="stat-completed" />
              <StatBox icon={Clock} label="حجوزات قيد التنفيذ" value={pending} accent="text-amber-600" testid="stat-pending" />
              <StatBox icon={Ruler} label="الكمية المتوفرة" value={`${p.quantity_available ?? 0} م`} testid="stat-available" />
              <StatBox icon={Eye} label="مرات الظهور بالبحث" value={searchCount} accent="text-[#088395]" testid="stat-search-count" />
              <StatBox icon={BarChart3} label="إجمالي الإيرادات" value={<span dir="ltr">${revenue}</span>} accent="text-[#088395]" testid="stat-revenue" />
            </div>

            {/* Recent sales */}
            <div>
              <h4 className="font-extrabold text-[#333333] mb-2">آخر المبيعات</h4>
              {recent.length === 0 ? (
                <div className="bg-[#EBF4F6] rounded-xl p-6 text-center text-[#666666] text-sm" data-testid="no-sales">
                  لا توجد عمليات بيع مكتملة بعد لهذا المنتج.
                </div>
              ) : (
                <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid="recent-sales-list">
                  <table className="w-full text-right text-sm">
                    <thead className="bg-[#EBF4F6]">
                      <tr>
                        <th className="p-2 font-bold">المشتري</th>
                        <th className="p-2 font-bold">الكمية</th>
                        <th className="p-2 font-bold">الإجمالي</th>
                        <th className="p-2 font-bold">التاريخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((r) => (
                        <tr key={r.id} className="border-t border-[#EBF4F6]">
                          <td className="p-2 font-bold">{r.requester_name}</td>
                          <td className="p-2">{r.meters} م</td>
                          <td className="p-2 text-[#09637E] font-bold" dir="ltr">${parseFloat(r.total_usd ?? 0).toFixed(2)}</td>
                          <td className="p-2 text-[#666666] text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("ar-SY") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoDashboardModal;
